import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../ui/card';
import { useLanguage } from '../../context/LanguageContext';
import { 
  Info, 
  BookOpen, 
  Database, 
  Lightbulb, 
  AlertCircle,
  ArrowRight,
  ExternalLink
} from 'lucide-react';

// Map related calculator display names (in any language) to the calc=<id>
// query param used by Verktyg.jsx to auto-open the calculator.
const NAME_TO_CALC_ID = {
  // Swedish
  'Virkesvolym': 'virkesvolym',
  'Virkesvärde': 'virkesvarde',
  'Avverkningsintäkt': 'avverkning',
  'Avverkning': 'avverkning',
  'Planteringskostnad': 'plantering',
  'Plantering': 'plantering',
  'Röjningskostnad': 'rojning',
  'Röjning': 'rojning',
  'Gallring': 'gallring',
  'Skogstillväxt': 'tillvaxt',
  'Tillväxt': 'tillvaxt',
  'Markvärde': 'markvarde',
  'Projektkostnad': 'projekt',
  'Skogsprojekt': 'projekt',
  'CO₂ kalkylator': 'co2',
  'CO2 kalkylator': 'co2',
  'CO₂': 'co2',
  'Fastighetsvärde': 'fastighetsvarde',
  'Pris per hektar': 'prisPerHektar',
  'Mark vs Virkesvärde': 'markVirke',
  'Mäklarprovision': 'provision',
  'Avancerad skogskalkyl': 'avancerad',
  'Jämför scenarier': 'jamfor',
  // English fallbacks
  'Timber volume': 'virkesvolym',
  'Timber value': 'virkesvarde',
  'Forest growth': 'tillvaxt',
  'CO₂ calculator': 'co2',
};

const calcIdForName = (name) => NAME_TO_CALC_ID[name] || null;

// Reusable component for calculator transparency and trust-building
export const CalculatorTransparency = ({ 
  assumptions = [],      // Array of { label, value, unit } objects
  howItWorks = '',       // String explaining how the calculation works
  sources = [],          // Array of source names/links
  didYouKnow = '',       // Educational fact
  relatedCalculators = [], // Array of { id, name, description } objects
  showDisclaimer = true
}) => {
  const { language } = useLanguage();

  const labels = {
    sv: {
      assumptions: 'Antaganden',
      howItWorks: 'Hur fungerar beräkningen?',
      sources: 'Källor',
      didYouKnow: 'Visste du?',
      disclaimer: 'Resultatet är en uppskattning baserad på standardvärden och bör inte ersätta professionell rådgivning.',
      relatedTools: 'Relaterade kalkylatorer',
      tryThis: 'Prova denna'
    },
    de: {
      assumptions: 'Annahmen',
      howItWorks: 'Wie funktioniert die Berechnung?',
      sources: 'Quellen',
      didYouKnow: 'Wussten Sie?',
      disclaimer: 'Das Ergebnis ist eine Schätzung basierend auf Standardwerten und sollte keine professionelle Beratung ersetzen.',
      relatedTools: 'Verwandte Kalkulatoren',
      tryThis: 'Probieren Sie'
    },
    en: {
      assumptions: 'Assumptions',
      howItWorks: 'How the calculation works',
      sources: 'Sources',
      didYouKnow: 'Did you know?',
      disclaimer: 'The result is an estimate based on standard values and should not replace professional advice.',
      relatedTools: 'Related calculators',
      tryThis: 'Try this'
    },
    se: {
      assumptions: 'Eaktudahttimat',
      howItWorks: 'Mo rehkenastit doaibmá?',
      sources: 'Gáldut',
      didYouKnow: 'Dihtet go?',
      disclaimer: 'Boađus lea árvvoštallan vuođđuduvvon standárdaárvvuide.',
      relatedTools: 'Gullevaš rehkenastit',
      tryThis: 'Geahččal dán'
    }
  };

  const t = labels[language] || labels.sv;

  return (
    <div className="space-y-4 mt-6" data-testid="calculator-transparency">
      {/* Disclaimer */}
      {showDisclaimer && (
        <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-amber-800">{t.disclaimer}</p>
        </div>
      )}

      {/* Assumptions Section */}
      {assumptions.length > 0 && (
        <Card className="bg-slate-50 border-slate-200">
          <CardContent className="p-4">
            <h4 className="font-semibold text-slate-700 flex items-center gap-2 mb-3">
              <Info className="h-4 w-4" />
              {t.assumptions}
            </h4>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {assumptions.map((item, index) => (
                <div key={index} className="text-sm">
                  <span className="text-muted-foreground">{item.label}: </span>
                  <span className="font-medium text-slate-700">
                    {item.value} {item.unit}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* How It Works Section */}
      {howItWorks && (
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-4">
            <h4 className="font-semibold text-blue-700 flex items-center gap-2 mb-2">
              <BookOpen className="h-4 w-4" />
              {t.howItWorks}
            </h4>
            <p className="text-sm text-blue-800 leading-relaxed">{howItWorks}</p>
          </CardContent>
        </Card>
      )}

      {/* Did You Know Section */}
      {didYouKnow && (
        <Card className="bg-emerald-50 border-emerald-200">
          <CardContent className="p-4">
            <h4 className="font-semibold text-emerald-700 flex items-center gap-2 mb-2">
              <Lightbulb className="h-4 w-4" />
              {t.didYouKnow}
            </h4>
            <p className="text-sm text-emerald-800 leading-relaxed">{didYouKnow}</p>
          </CardContent>
        </Card>
      )}

      {/* Sources Section */}
      {sources.length > 0 && (
        <Card className="bg-gray-50 border-gray-200">
          <CardContent className="p-4">
            <h4 className="font-semibold text-gray-700 flex items-center gap-2 mb-2">
              <Database className="h-4 w-4" />
              {t.sources}
            </h4>
            <ul className="text-sm text-gray-600 space-y-1">
              {sources.map((source, index) => (
                <li key={index} className="flex items-center gap-1">
                  <span className="w-1.5 h-1.5 bg-gray-400 rounded-full" />
                  {source.url ? (
                    <a 
                      href={source.url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="hover:text-forest-600 hover:underline flex items-center gap-1"
                    >
                      {source.name}
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  ) : (
                    <span>{source.name || source}</span>
                  )}
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {/* Related Calculators Section */}
      {relatedCalculators.length > 0 && (
        <Card className="bg-forest-50 border-forest-200">
          <CardContent className="p-4">
            <h4 className="font-semibold text-forest-700 flex items-center gap-2 mb-3">
              <ArrowRight className="h-4 w-4" />
              {t.relatedTools}
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
              {relatedCalculators.map((calc, index) => {
                const calcId = calc.id || calcIdForName(calc.name);
                const href = calcId ? `/verktyg?calc=${calcId}` : '/verktyg';
                return (
                  <Link 
                    key={index}
                    to={href}
                    data-testid={`related-calc-${calcId || index}`}
                    className="p-2 bg-white rounded border border-forest-200 hover:border-forest-400 hover:shadow-sm transition-all group"
                  >
                    <p className="text-sm font-medium text-forest-700 group-hover:text-forest-800">
                      {calc.name}
                    </p>
                    {calc.description && (
                      <p className="text-xs text-muted-foreground">{calc.description}</p>
                    )}
                  </Link>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// Predefined transparency data for different calculator types
export const transparencyData = {
  virkesvolym: {
    sv: {
      howItWorks: 'Denna kalkylator uppskattar virkesvolym baserat på trädets diameter (brösthöjdsdiameter), höjd och formfaktor enligt Brandels volymfunktioner som är standard inom svenskt skogsbruk.',
      didYouKnow: 'En medelgran i Sverige har ofta en diameter på 20-30 cm och en höjd på 18-25 meter. Formfaktorn (0.40-0.45) beskriver hur mycket av en cylinder som trädet faktiskt fyller.',
      sources: [
        { name: 'Skogsstyrelsen - Volymfunktioner', url: 'https://www.skogsstyrelsen.se' },
        { name: 'SLU Riksskogstaxeringen' },
        { name: 'Brandels volymfunktioner (1990)' }
      ],
      relatedCalculators: [
        { name: 'Virkesvärde', description: 'Beräkna värdet på ditt virke' },
        { name: 'Skogstillväxt', description: 'Uppskatta framtida volym' },
        { name: 'CO₂ kalkylator', description: 'Beräkna kollagring' }
      ]
    },
    de: {
      howItWorks: 'Dieser Rechner schätzt das Holzvolumen basierend auf Durchmesser, Höhe und Formfaktor nach Brandels Volumenfunktionen.',
      didYouKnow: 'Eine durchschnittliche Fichte in Schweden hat oft einen Durchmesser von 20-30 cm und eine Höhe von 18-25 Metern.',
      sources: [
        { name: 'Skogsstyrelsen - Volumenfunktionen' },
        { name: 'SLU Reichswaldinventur' }
      ]
    },
    en: {
      howItWorks: 'This calculator estimates timber volume based on tree diameter (DBH), height, and form factor according to Brandel\'s volume functions used in Swedish forestry.',
      didYouKnow: 'An average spruce in Sweden typically has a diameter of 20-30 cm and a height of 18-25 meters. The form factor (0.40-0.45) describes how much of a cylinder the tree actually fills.',
      sources: [
        { name: 'Swedish Forest Agency - Volume Functions' },
        { name: 'SLU National Forest Inventory' }
      ]
    }
  },
  virkesvarde: {
    sv: {
      howItWorks: 'Kalkylatorn beräknar virkesvärde genom att multiplicera volym med aktuella marknadspriser för sågtimmer och massaved. Fördelningen mellan sortimenten baseras på typiska utfall för respektive trädslag.',
      didYouKnow: 'Sågtimmer (stockar över 14 cm) är betydligt mer värdefullt än massaved. En genomsnittlig slutavverkning kan ge 60% sågtimmer och 40% massaved för gran.',
      sources: [
        { name: 'Skogsstyrelsen - Virkesstatistik', url: 'https://www.skogsstyrelsen.se' },
        { name: 'VMK - Virkespriser' },
        { name: 'SDC - Skogsdata' }
      ],
      relatedCalculators: [
        { name: 'Virkesvolym', description: 'Beräkna kubikinnehåll' },
        { name: 'Avverkningsintäkt', description: 'Nettointäkt efter kostnader' },
        { name: 'Fastighetsvärde', description: 'Totalt skogsvärde' }
      ]
    }
  },
  avverkning: {
    sv: {
      howItWorks: 'Kalkylatorn beräknar nettointäkt från avverkning genom att dra av drivningskostnader (avverkning och transport) från bruttointäkten. Kostnaderna varierar beroende på terräng och avverkningsmetod.',
      didYouKnow: 'Drivningskostnaderna i Sverige ligger normalt mellan 150-250 SEK/m³fub beroende på terrängsvårighetsgrad. Slutavverkning är billigare per kubikmeter än gallring.',
      sources: [
        { name: 'Skogforsk - Drivningskostnader' },
        { name: 'Skogsstyrelsen - Statistik' }
      ],
      relatedCalculators: [
        { name: 'Gallring', description: 'Gallringsberäkning' },
        { name: 'Virkesvärde', description: 'Virkets värde' },
        { name: 'Projektkostnad', description: 'Budgetera projekt' }
      ]
    }
  },
  plantering: {
    sv: {
      howItWorks: 'Kalkylatorn beräknar totalkostnaden för återbeskogning inklusive markberedning, plantor och planteringsarbete. Planttätheten varierar beroende på trädslag och förväntad avgång.',
      didYouKnow: 'I Sverige planteras normalt 2000-2500 plantor per hektar för gran och tall. Med förväntad avgång på 20-30% når man ett slutbestånd på ca 1500-2000 stammar.',
      sources: [
        { name: 'Skogsstyrelsen - Återbeskogning' },
        { name: 'Plantskoleförbundet' }
      ],
      relatedCalculators: [
        { name: 'Röjningskostnad', description: 'Nästa skötselåtgärd' },
        { name: 'Skogstillväxt', description: 'Framtida utveckling' },
        { name: 'Projektkostnad', description: 'Helhetsbild' }
      ]
    }
  },
  rojning: {
    sv: {
      howItWorks: 'Kalkylatorn uppskattar kostnaden för röjning baserat på röjningsintensitet, terräng och metod. Röjning utförs normalt när beståndet är 2-4 meter högt.',
      didYouKnow: 'Röjning är en investering som höjer värdet på framtida avverkningar med 20-50%. En bra röjning ger kvarvarande träd bättre tillväxtförutsättningar.',
      sources: [
        { name: 'Skogsstyrelsen - Röjningsrekommendationer' },
        { name: 'SLU - Ungskogsskötsel' }
      ],
      relatedCalculators: [
        { name: 'Plantering', description: 'Förra skötselmomentet' },
        { name: 'Gallring', description: 'Nästa skötselmoment' },
        { name: 'Projektkostnad', description: 'Total kostnadsbild' }
      ]
    }
  },
  gallring: {
    sv: {
      howItWorks: 'Kalkylatorn beräknar utfall och intäkt från gallring. Gallring innebär att man tar ut 25-35% av volymen för att ge kvarvarande träd bättre tillväxt. Nettot beror på uttag och drivningskostnader.',
      didYouKnow: 'En skog gallras normalt 1-3 gånger under omloppstiden. Första gallring sker vid ca 30-40 års ålder för gran i södra Sverige.',
      sources: [
        { name: 'Skogforsk - Gallringsmallar' },
        { name: 'Skogsstyrelsen - Gallringsråd' }
      ],
      relatedCalculators: [
        { name: 'Röjningskostnad', description: 'Föregående skötsel' },
        { name: 'Avverkningsintäkt', description: 'Slutavverkning' },
        { name: 'Skogstillväxt', description: 'Tillväxt efter gallring' }
      ]
    }
  },
  tillvaxt: {
    sv: {
      howItWorks: 'Kalkylatorn uppskattar framtida virkesvolym baserat på nuvarande volym, bonitet (markens produktionsförmåga) och skötselintensitet. Tillväxten varierar med trädslag, ålder och region.',
      didYouKnow: 'En gran i Sverige kan växa mellan 3-10 m³sk per hektar och år beroende på markens bonitet. Södra Sverige har generellt högre bonitet än norra.',
      sources: [
        { name: 'SLU Riksskogstaxeringen - Tillväxtdata' },
        { name: 'Heureka - Tillväxtmodeller' },
        { name: 'Skogsstyrelsen - Bonitetsklasser' }
      ],
      relatedCalculators: [
        { name: 'Virkesvolym', description: 'Nuvarande volym' },
        { name: 'Virkesvärde', description: 'Framtida värde' },
        { name: 'CO₂ kalkylator', description: 'Kolbindning över tid' }
      ]
    }
  },
  markvarde: {
    sv: {
      howItWorks: 'Kalkylatorn uppskattar markvärde med Faustmanns formel som beräknar nuvärdet av framtida skogsbruksintäkter minus kostnader över en evig tidshorisont.',
      didYouKnow: 'Markvärdet representerar produktionskapaciteten hos skogsmarken. Bra bonitet ger högre markvärde oavsett nuvarande virkesförråd.',
      sources: [
        { name: 'Faustmanns formel (1849)' },
        { name: 'SLU - Skogsekonomi' },
        { name: 'LRF - Markvärdestabeller' }
      ],
      relatedCalculators: [
        { name: 'Fastighetsvärde', description: 'Totalt värde inkl. virke' },
        { name: 'Skogstillväxt', description: 'Framtida produktion' },
        { name: 'Avverkningsintäkt', description: 'Förväntad intäkt' }
      ]
    }
  },
  co2: {
    sv: {
      howItWorks: 'Kalkylatorn beräknar kollagring i skog baserat på virkesvolym. Varje kubikmeter trä binder cirka 0.9 ton CO₂. Värdet baseras på EU:s utsläppshandelsystem (EU ETS).',
      didYouKnow: 'En hektar svensk skog binder i genomsnitt 5-10 ton CO₂ per år genom tillväxt. Det motsvarar utsläppen från en genomsnittlig svensk personbil som kör 30 000-60 000 km.',
      sources: [
        { name: 'IPCC - Carbon accounting' },
        { name: 'EU ETS - Utsläppsrätter' },
        { name: 'SLU - Kolbalansstudier' }
      ],
      relatedCalculators: [
        { name: 'Skogstillväxt', description: 'Kolbindning över tid' },
        { name: 'Virkesvolym', description: 'Beräkna kolförråd' },
        { name: 'Virkesvärde', description: 'Ekonomiskt värde' }
      ]
    }
  },
  fastighetsvarde: {
    sv: {
      howItWorks: 'Kalkylatorn uppskattar skogsfastighetens totala värde baserat på virkesförråd, bonitet, åldersfördelning och markvärde. Priset varierar betydligt mellan regioner.',
      didYouKnow: 'Skogsfastigheter i Sverige säljs ofta för 400-800 SEK per m³sk beroende på läge och kvalitet. Södra Sverige har generellt högre priser än norra.',
      sources: [
        { name: 'LRF Konsult - Prisstatistik' },
        { name: 'Ludvig & Co - Marknadspriser' },
        { name: 'Skogssällskapet - Värdering' }
      ],
      relatedCalculators: [
        { name: 'Pris per hektar', description: 'Jämförelsetal' },
        { name: 'Mark vs Virkesvärde', description: 'Värdefördelning' },
        { name: 'Mäklarprovision', description: 'Försäljningskostnad' }
      ]
    }
  },
  prisPerHektar: {
    sv: {
      howItWorks: 'Kalkylatorn beräknar jämförelsetal för skogsfastigheter genom att dividera totalpris med areal (SEK/ha) och med virkesförråd (SEK/m³sk).',
      didYouKnow: 'Pris per m³sk är det vanligaste jämförelsetalet vid skogsfastighetsaffärer. Det varierar från ca 350 kr i Norrlands inland till över 800 kr i Sydsverige.',
      sources: [
        { name: 'LRF Konsult - Fastighetsstatistik' },
        { name: 'Skogssällskapet - Marknadsanalys' }
      ],
      relatedCalculators: [
        { name: 'Fastighetsvärde', description: 'Totalvärdering' },
        { name: 'Mark vs Virkesvärde', description: 'Värdeanalys' },
        { name: 'Mäklarprovision', description: 'Provision' }
      ]
    }
  },
  markVirke: {
    sv: {
      howItWorks: 'Kalkylatorn analyserar fördelningen mellan markvärde och virkesvärde på en skogsfastighet. Markvärdet representerar produktionskapaciteten medan virkesvärdet är det stående virkesförrådet.',
      didYouKnow: 'På en genomsnittlig skogsfastighet utgör markvärdet ofta 15-25% av totalvärdet. Unga skogar har högre andel markvärde medan äldre avverkningsmogna skogar domineras av virkesvärde.',
      sources: [
        { name: 'SLU - Fastighetsvärdering' },
        { name: 'LRF Konsult' }
      ],
      relatedCalculators: [
        { name: 'Markvärde', description: 'Detaljerad markberäkning' },
        { name: 'Virkesvärde', description: 'Värde på stående skog' },
        { name: 'Fastighetsvärde', description: 'Totalvärde' }
      ]
    }
  },
  provision: {
    sv: {
      howItWorks: 'Kalkylatorn beräknar skogsmäklarens provision baserat på försäljningspris och provisionsavtal. Standardprovision ligger normalt mellan 2.5-5% av köpeskillingen.',
      didYouKnow: 'Vid större fastighetsaffärer (över 10 miljoner kr) är det vanligt att förhandla om lägre provisionssats. Provisionen inkluderar normalt marknadsföring, visningar och kontraktshantering.',
      sources: [
        { name: 'FMI - Fastighetsmäklarinspektionen' },
        { name: 'Mäklarsamfundet' }
      ],
      relatedCalculators: [
        { name: 'Fastighetsvärde', description: 'Uppskatta försäljningspris' },
        { name: 'Pris per hektar', description: 'Jämförelsetal' }
      ]
    }
  }
};

export default CalculatorTransparency;
